import { AxiosError } from 'axios';
import useSWR from 'swr';

import { capitalize, WithStatusT } from '@consumer-frontend/nc-utils';

import { apiEndpoints } from '../lib';

type DebitCardT = {
  card_brand: string;
  expiration_date: string; // Format is MM/YY
  is_eligible_for_update: boolean;
  is_valid: boolean;
  last4: string;
};

type BankAccountT = {
  bank_name: string;
  is_eligible_for_update: boolean;
  is_valid: boolean;
  last4_account_number: string;
  last4_routing_number: string;
  type?: 'checking' | 'savings';
};

type PaymentMethodsResponseT = {
  current_bank_account: BankAccountT;
  current_debit_card: DebitCardT | null;
};

export const usePaymentMethods = (accountId: string) => {
  // eslint-disable-next-line @stylistic/object-curly-newline
  const { data, error, isLoading } = useSWR<WithStatusT<PaymentMethodsResponseT>, AxiosError>(
    () => (accountId ? apiEndpoints.dashboard.paymentMethods(accountId) : null),
  );

  const currentBankAccount = data?.current_bank_account
    ? {
      bankAccountType: capitalize(data.current_bank_account.type),
      bankName: data.current_bank_account.bank_name,
      isEligibleForUpdate: data.current_bank_account.is_eligible_for_update,
      isValid: data.current_bank_account.is_valid,
      last4AccountNumber: data.current_bank_account.last4_account_number,
      last4RoutingNumber: data.current_bank_account.last4_routing_number,
    }
    : undefined;

  const currentDebitCard = data?.current_debit_card
    ? {
      cardBrand: data.current_debit_card.card_brand,
      expirationDate: data.current_debit_card.expiration_date,
      isEligibleForUpdate: data.current_debit_card.is_eligible_for_update,
      isValid: data.current_debit_card.is_valid,
      last4: data.current_debit_card.last4,
    }
    : undefined;

  return {
    currentBankAccount,
    currentDebitCard,
    error,
    isLoading,
    status: data?.status || error?.status,
  };
};
